import { QueryClient } from '@tanstack/react-query';

import { useNonBenefitMapNearbyPharmacySearchTabsQuery } from '@/hooks/nonBenefit/nonBenefit.gql.impl';
import { NearbyPharmacyFilterModel } from '@/models/pharmacy';
import {
  NonBenefitMapNearbyPharmacySearchTabsQuery,
  PharmacyFilterMode,
  PharmacyFilterType,
  PharmacySortType,
} from '@/types/bff/gql-schema';
import {
  ENearbyPharmacySearchFilterType,
  ENearbyPharmacySearchSortType,
  EPharmacyFilterMode,
  NearbyPharmacySearchTabsVo,
  NearbyPharmacySortsVo,
} from '@/types/pharmacy';

// 타입 변환 매퍼 함수
const mapToPharmacyFilterMode = (mode: PharmacyFilterMode): EPharmacyFilterMode => {
  // PharmacyFilterMode와 EPharmacyFilterMode는 동일한 값을 가지므로 직접 매핑 가능
  switch (mode) {
    case PharmacyFilterMode.List:
      return EPharmacyFilterMode.List;
    case PharmacyFilterMode.Toggle:
      return EPharmacyFilterMode.Toggle;
    case PharmacyFilterMode.Range:
      return EPharmacyFilterMode.Range;
    default:
      return EPharmacyFilterMode.Toggle;
  }
};

const mapToPharmacySearchFilterType = (type: PharmacyFilterType): ENearbyPharmacySearchFilterType => {
  // PharmacyFilterType을 ENearbyPharmacySearchFilterType으로 매핑
  switch (type) {
    case PharmacyFilterType.ConfirmedDispensation:
      return ENearbyPharmacySearchFilterType.ConfirmedDispensation;
    case PharmacyFilterType.DispensationProbability:
      return ENearbyPharmacySearchFilterType.DispensationProbability;
    case PharmacyFilterType.HandlingMedicine:
      return ENearbyPharmacySearchFilterType.HandlingMedicine;
    case PharmacyFilterType.HighDispensationProbability:
      return ENearbyPharmacySearchFilterType.HighDispensationProbability;
    case PharmacyFilterType.IncludeDiagnosisPrice:
      return ENearbyPharmacySearchFilterType.IncludeDiagnosisPrice;
    case PharmacyFilterType.IsOpenAtLateNight:
      return ENearbyPharmacySearchFilterType.IsOpenAtLateNight;
    case PharmacyFilterType.IsOpenEveryDay:
      return ENearbyPharmacySearchFilterType.IsOpenEveryDay;
    case PharmacyFilterType.Now:
      return ENearbyPharmacySearchFilterType.Now;
    case PharmacyFilterType.Operating:
      return ENearbyPharmacySearchFilterType.Operating;
    case PharmacyFilterType.PossibleDispensation:
      return ENearbyPharmacySearchFilterType.PossibleDispensation;
    case PharmacyFilterType.RequestWithApp:
      return ENearbyPharmacySearchFilterType.RequestWithApp;
    case PharmacyFilterType.SameMedicine:
      return ENearbyPharmacySearchFilterType.SameMedicine;
    // FavoritePharmacy, Partnership, TreatmentMedicine는 없으므로 기본값 반환
    default:
      return ENearbyPharmacySearchFilterType.CheapPrice;
  }
};

const mapToPharmacySortsVo = (sort: {
  type: PharmacySortType;
  title: string;
}): NearbyPharmacySortsVo => {
  // PharmacySortType을 ENearbyPharmacySearchSortType으로 매핑
  let sortType: ENearbyPharmacySearchSortType;

  switch (sort.type) {
    case PharmacySortType.Distance:
      sortType = ENearbyPharmacySearchSortType.Distance;
      break;
    case PharmacySortType.LowPrice:
      sortType = ENearbyPharmacySearchSortType.LowPrice;
      break;
    case PharmacySortType.QuickReceipt:
      sortType = ENearbyPharmacySearchSortType.QuickReceipt;
      break;
    case PharmacySortType.VisitCount:
      sortType = ENearbyPharmacySearchSortType.VisitCount;
      break;
    case PharmacySortType.MostReviews:
      sortType = ENearbyPharmacySearchSortType.MostReviews;
      break;
    case PharmacySortType.HighRating:
      sortType = ENearbyPharmacySearchSortType.HighRating;
      break;
    // DispensationProbability는 ENearbyPharmacySearchSortType에 없음 -> 기본값 사용
    default:
      sortType = ENearbyPharmacySearchSortType.QuickReceipt;
  }

  return {
    title: sort.title,
    type: sortType,
  };
};

const parseNonBenefitMapNearByPharmacySearchTabsVo = (
  data: NonBenefitMapNearbyPharmacySearchTabsQuery
): NearbyPharmacySearchTabsVo[] => {
  return data.nonBenefitMapNearbyPharmacySearchTabs.map((item) => {
    return {
      id: item.id,
      name: item.name,
      diagnosisType: item.diagnosisType,
      diagnosisTypeId: item.diagnosisTypeId,
      filters: item.filters.map((filter) => {
        return new NearbyPharmacyFilterModel({
          activeIconUrl: filter.activeIconUrl ?? undefined,
          inactiveIconUrl: filter.inactiveIconUrl ?? undefined,
          mode: mapToPharmacyFilterMode(filter.mode),
          title: filter.title ?? '',
          description: filter.description ?? '',
          type: mapToPharmacySearchFilterType(filter.type),
          subFilters: filter.subFilters.map((subFilter) => {
            return new NearbyPharmacyFilterModel({
              mode: mapToPharmacyFilterMode(subFilter.mode),
              title: subFilter.title ?? '',
              description: subFilter.description ?? '',
              type: mapToPharmacySearchFilterType(subFilter.type),
              subFilters: [],
            });
          }),
        });
      }),
      sortTypes: item.sortTypes.map((type) => mapToPharmacySortsVo(type)),
    };
  });
};
const useNonBenefitMapNearByPharmacySearchTabs = () => {
  const { data } = useNonBenefitMapNearbyPharmacySearchTabsQuery(
    {},
    {
      select: parseNonBenefitMapNearByPharmacySearchTabsVo,
      staleTime: 1000 * 60 * 5,
    }
  );

  return {
    data,
  };
};

export default useNonBenefitMapNearByPharmacySearchTabs;

const prefetchQuery = async (queryClient: QueryClient) => {
  await queryClient.prefetchQuery(
    useNonBenefitMapNearbyPharmacySearchTabsQuery.getKey({}),
    useNonBenefitMapNearbyPharmacySearchTabsQuery.fetcher({})
  );
};

useNonBenefitMapNearByPharmacySearchTabs.prefetchQuery = prefetchQuery;
